import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { PageHeader } from '@multaqa/ui/components/ui/page-header'
import type { ReactNode } from 'react'

const DEFAULT_SKELETON_COLUMNS = [1.5, 1.5, 1, 1, 1]

interface AdminScreenProps {
  /** Breadcrumb trail passed straight to PageHeader. */
  trail?: string[]
  title: string
  description?: string
  /** Rendered at the end of the header row. */
  actions?: ReactNode
  /** Shown instead of children while true. */
  loading?: boolean
  /** Skeleton column weights used while loading. */
  skeletonColumns?: number[]
  /** Non-null renders the error state instead of children. */
  error?: unknown
  onRetry?: () => void
  testId?: string
  children: ReactNode
}

/**
 * The one screen frame every admin screen renders inside.
 *
 * Before this, each screen re-implemented its own header, padding, loading
 * and error state with slightly different values — `p-4` in `groups.tsx`,
 * `p-[18px]` in `dashboard.tsx`, `px-2 py-4 lg:px-6` in the onboarding
 * wizard. That inconsistency, more than any single screen, is why the app
 * reads as unfinished. `AdminScreen` is the one frame; a screen supplies its
 * title, its query state, and its content, and gets the same header, the
 * same `p-4` body inset and the same `ListSkeleton`/`AppError` treatment as
 * every other screen.
 *
 * The header renders unconditionally — only the body switches between the
 * skeleton, the error surface and the real content — so a screen's title and
 * breadcrumb trail never disappear while its data loads or fails.
 */
export function AdminScreen({
  trail,
  title,
  description,
  actions,
  loading = false,
  skeletonColumns = DEFAULT_SKELETON_COLUMNS,
  error,
  onRetry,
  testId,
  children,
}: AdminScreenProps) {
  const messages = useAppErrorMessages()
  const hasError = error !== null && error !== undefined
  const bodyTestId = testId ? `${testId}-body` : undefined

  return (
    <div className="bg-background flex h-full min-h-0 flex-col" data-testid={testId}>
      <PageHeader
        trail={trail}
        title={title}
        description={description}
        actions={actions}
        className="border-b border-border px-4 pb-[18px]"
      />

      {hasError ? (
        <AppError error={error} messages={messages} retry={onRetry} />
      ) : loading ? (
        <div className="p-4" data-testid={bodyTestId}>
          <ListSkeleton columns={skeletonColumns} rows={8} />
        </div>
      ) : (
        <div className="p-4" data-testid={bodyTestId}>
          {children}
        </div>
      )}
    </div>
  )
}
